import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
import "./ProfilePage.css";
import { WA_CS_LINK as WA_LINK } from "../config/whatsapp";
import { LANGUAGES, useLanguage } from "../i18n/LanguageContext";
import { IcGlobe, LanguageSheet } from "../i18n/LanguageSwitcher";
const IcCoin = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="#237804" strokeWidth="2" />
        <path d="M14.5 9.5a2.5 2.5 0 00-2.5-1.5c-1.4 0-2.5.9-2.5 2s1.1 2 2.5 2 2.5.9 2.5 2-1.1 2-2.5 2a2.5 2.5 0 01-2.5-1.5M12 6.5v11" stroke="#237804" strokeWidth="2" strokeLinecap="round" />
    </svg>
);
const IcMail = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="3" stroke="#237804" strokeWidth="2" />
        <path d="M4 7l8 6 8-6" stroke="#237804" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const IcChevron = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <polyline points="9 6 15 12 9 18" stroke="#bfbfbf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const IcLogout = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M14 8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2v-2" stroke="#cf1322" strokeWidth="2" strokeLinecap="round" />
        <path d="M18 15l3-3-3-3M21 12H9" stroke="#cf1322" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const IcCheck = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <polyline points="5 12 10 17 19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Language is the one preference that matters before signing in, so this card
// renders in both the signed-in and signed-out views.
const PreferencesSection = ({ className = "" }) => {
    const { lang, t } = useLanguage();
    const [sheetOpen, setSheetOpen] = useState(false);
    const current = LANGUAGES.find((l) => l.code === lang);

    return (
        <div className={`pf-section ${className}`.trim()}>
            <p className="pf-section-title">{t("profile.preferences")}</p>
            <div className="pf-card">
                <button
                    type="button"
                    className="pf-row pf-row-link"
                    onClick={() => setSheetOpen(true)}
                    aria-haspopup="dialog"
                >
                    <span className="pf-row-icon"><IcGlobe size={20} color="#237804" /></span>
                    <span className="pf-row-label">{t("language.label")}</span>
                    <span className="pf-row-badge">{current.label}</span>
                    <span className="pf-row-chev"><IcChevron /></span>
                </button>
                <div className="pf-row pf-row-static">
                    <span className="pf-row-icon"><IcCoin /></span>
                    <span className="pf-row-label">{t("profile.currency")}</span>
                    <span className="pf-row-badge">{t("profile.currencyValue")}</span>
                </div>
            </div>
            <LanguageSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
        </div>
    );
};

const ProfilePage = () => {
    const { user, isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth0();
    const { pathname, search } = useLocation();
    const { t } = useLanguage();

    if (isLoading) {
        return (
            <div className="pf-page">
                <div className="pf-content">
                    <div className="pf-skel pf-skel-header" />
                    <div className="pf-skel pf-skel-card" />
                    <div className="pf-skel pf-skel-card" />
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="pf-page">
                <div className="pf-content pf-content-centered">
                    <div className="pf-login-icon">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <circle cx="12" cy="8" r="4" stroke="#237804" strokeWidth="1.8" />
                            <path d="M4 20a8 8 0 0116 0" stroke="#237804" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                    </div>
                    <h2 className="pf-login-title">{t("profile.loginTitle")}</h2>
                    <p className="pf-login-desc">{t("profile.loginDesc")}</p>
                    <button
                        className="pf-login-btn"
                        onClick={() => {
                            localStorage.setItem("redirectAfterLogin", `${pathname}${search}`);
                            loginWithRedirect().catch((e) => console.error("Login error:", e));
                        }}
                    >
                        {t("common.login")}
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M5 12h14M13 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>

                    <PreferencesSection className="pf-section-standalone" />
                </div>
            </div>
        );
    }

    const initials = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();

    return (
        <div className="pf-page">
            <div className="pf-content">
                {/* Header */}
                <div className="pf-header">
                    <div className="pf-avatar-wrap">
                        {user?.picture
                            ? <img className="pf-avatar" src={user.picture} alt={user.name || t("profile.avatarAlt")} />
                            : <div className="pf-avatar pf-avatar-ph">{initials}</div>}
                        <span className="pf-avatar-check"><IcCheck /></span>
                    </div>
                    <div className="pf-header-text">
                        <h1 className="pf-name">{user?.name || t("profile.userFallback")}</h1>
                        {user?.email && <p className="pf-email">{user.email}</p>}
                    </div>
                </div>

                <PreferencesSection />

                {/* Dukungan */}
                <div className="pf-section">
                    <p className="pf-section-title">{t("profile.support")}</p>
                    <div className="pf-card">
                        <a className="pf-row pf-row-link" href={WA_LINK} target="_blank" rel="noopener noreferrer">
                            <span className="pf-row-icon"><IcMail /></span>
                            <span className="pf-row-label">{t("profile.contact")}</span>
                            <span className="pf-row-chev"><IcChevron /></span>
                        </a>
                    </div>
                </div>

                {/* Logout */}
                <button
                    className="pf-logout"
                    onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                >
                    <IcLogout /> {t("profile.logout")}
                </button>
            </div>
        </div>
    );
};

export default ProfilePage;
